import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { newsList } from "@/mocks/news";

const PAGE_SIZE = 6;

const categories = [
  { value: "all", label: "全部" },
  { value: "news", label: "新闻" },
  { value: "review", label: "评论" },
  { value: "preview", label: "预告" },
];

const sorts = [
  { value: "newest", label: "最新发布" },
  { value: "oldest", label: "最早发布" },
];

export default function NewsListPage() {
  useDocumentMeta(
    "动漫资讯 - h动漫",
    "浏览h动漫最新动漫新闻、评论和预告，获取丰富动漫资讯。"
  );

  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("newest");
  const [tag, setTag] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    newsList.forEach((n) => n.tags.forEach((t) => set.add(t)));
    return Array.from(set).slice(0, 12);
  }, []);

  const filtered = useMemo(() => {
    let list = newsList.filter((n) => (category === "all" ? true : n.category === category));
    if (tag) list = list.filter((n) => n.tags.includes(tag));
    list = [...list].sort((a, b) =>
      sort === "newest" ? b.publishedAt.localeCompare(a.publishedAt) : a.publishedAt.localeCompare(b.publishedAt)
    );
    return list;
  }, [category, sort, tag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const reset = () => {
    setCategory("all");
    setSort("newest");
    setTag(null);
    setPage(1);
  };

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动漫资讯" }]} />
      <header className="mt-4">
        <h1 className="font-heading font-extrabold text-foreground-950 text-2xl md:text-3xl">动漫资讯</h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          汇集最新动漫新闻、深度评论与开播预告。可按类别、时间与关键词标签筛选，快速找到你关心的动漫动态。
        </p>
      </header>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Filter panel */}
        <aside className="lg:sticky lg:top-20 self-start space-y-4">
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
            <h2 className="flex items-center gap-2 font-heading font-semibold text-foreground-950 text-sm mb-3">
              <i className="ri-filter-3-line text-primary-500" />
              筛选条件
            </h2>

            <div className="space-y-3">
              <div>
                <p className="text-xs font-medium text-foreground-700 mb-2">资讯类别</p>
                <div className="flex flex-wrap gap-1.5">
                  {categories.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => {
                        setCategory(c.value);
                        setPage(1);
                      }}
                      className={`px-3 py-1.5 rounded-md text-xs cursor-pointer transition-colors ${
                        category === c.value
                          ? "bg-primary-500 text-background-50 font-medium"
                          : "bg-background-100 text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-foreground-700 mb-2">发布时间</p>
                <div className="flex flex-wrap gap-1.5">
                  {sorts.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => {
                        setSort(s.value);
                        setPage(1);
                      }}
                      className={`px-3 py-1.5 rounded-md text-xs cursor-pointer transition-colors ${
                        sort === s.value
                          ? "bg-accent-500 text-background-50 font-medium"
                          : "bg-background-100 text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-foreground-700 mb-2">关键词标签</p>
                <div className="flex flex-wrap gap-1.5">
                  {allTags.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        setTag(tag === t ? null : t);
                        setPage(1);
                      }}
                      className={`px-2.5 py-1 rounded-full text-[11px] cursor-pointer transition-colors ${
                        tag === t
                          ? "bg-secondary-500 text-background-50"
                          : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                      }`}
                    >
                      #{t}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={reset}
                className="w-full h-9 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选
              </button>
            </div>
          </div>
        </aside>

        {/* List */}
        <div className="min-w-0">
          <div className="mb-4 text-sm text-foreground-600">
            共找到 <span className="font-semibold text-foreground-900">{filtered.length}</span> 条资讯
            {tag && <span className="ml-1">· 标签 #{tag}</span>}
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-3 md:gap-4">
                {pageItems.map((news) => (
                  <NewsCard key={news.id} news={news} />
                ))}
              </div>
              <div className="mt-8">
                <Pagination current={safePage} total={totalPages} onChange={setPage} />
              </div>
            </>
          ) : (
            <EmptyState
              icon="ri-newspaper-line"
              title="暂无相关资讯"
              description="换个类别或标签试试，或者重置筛选条件查看全部资讯。"
              actionLabel="重置筛选"
              onAction={reset}
            />
          )}
        </div>
      </div>
    </div>
  );
}